import { format } from 'date-fns'
import jsPDF from 'jspdf'
import autoTable, { CellInput } from 'jspdf-autotable'
import { FinancialReport } from '../repositories/ReportRepository'

// --- helpers ---
const isFiniteNumber = (v: unknown): v is number => Number.isFinite(v as number)

/** Draw a rounded rect only when all args are valid positive numbers */
function safeRoundedRect(
  doc: jsPDF,
  x?: number,
  y?: number,
  w?: number,
  h?: number,
  rx = 10,
  ry = 10,
  style: 'S' | 'F' | 'DF' | 'FD' = 'F'
) {
  if (
    isFiniteNumber(x) &&
    isFiniteNumber(y) &&
    isFiniteNumber(w) &&
    isFiniteNumber(h) &&
    w > 0 &&
    h > 0
  ) {
    doc.roundedRect(x, y, w, h, rx, ry, style)
  }
}

// --- Detailed Owner Report PDF ---
export async function exportOwnerReportToPDF(
  reportData: FinancialReport,
  startDate: Date | null,
  endDate: Date | null
) {
  const doc = new jsPDF({ unit: 'pt' })
  const pageHeight = doc.internal.pageSize.height
  const pageWidth = doc.internal.pageSize.width
  const margin = 70.86 // 2.5cm in points
  let cursorY = margin + 10

  // --- Logo ---
  try {
    const response = await fetch('/logo_infinity.png')
    const blob = await response.blob()
    const reader = new FileReader()
    await new Promise<void>((resolve, reject) => {
      reader.onload = () => {
        doc.addImage(
          reader.result as string,
          'PNG',
          margin - 50,
          margin - 65,
          100,
          0
        )
        resolve()
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error loading logo:', error)
    // Continue without the logo if it fails to load
  }

  // --- Color Palette ---
  const colors = {
    darkBlue: '#1F4E79',
    lightGray: '#F2F2F2',
    lightBlue: '#E6F0FA',
    veryLightGray: '#FAFAFA',
    green: '#228B22',
    red: '#FF0000',
    gray: '#808080',
    mediumGray: '#A9A9A9',
    divider: '#DDDDDD',
  }

  // --- Header & Footer ---
  const addFooter = () => {
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(9).setTextColor(colors.mediumGray)
      doc.text(
        'Infinity Soluções, Todos os direitos reservados.',
        pageWidth - margin,
        pageHeight - margin + 30,
        { align: 'right' }
      )
    }
  }

  // --- PDF Content ---

  // Generation Date (Top Right)
  doc.setFontSize(10).setTextColor(colors.gray)
  doc.text(
    `Gerado dia: ${format(new Date(), 'dd/MM/yyyy')} as ${format(
      new Date(),
      'HH:mm'
    )}`,
    pageWidth - margin,
    margin - 35,
    { align: 'right' }
  )

  // Title
  doc.setFontSize(20).setFont('helvetica', 'bold').setTextColor(colors.darkBlue)
  doc.text(`Relatório de ${reportData.owner_name}`, pageWidth / 2, cursorY, {
    align: 'center',
  })
  cursorY += 20

  // Subtitle
  if (startDate && endDate) {
    doc.setFontSize(12).setFont('helvetica', 'italic').setTextColor(colors.gray)
    doc.text(
      `Período: ${format(startDate, 'dd/MM/yyyy')} a ${format(
        endDate,
        'dd/MM/yyyy'
      )}`,
      pageWidth / 2,
      cursorY,
      { align: 'center' }
    )
  }
  cursorY += 30

  // Summary Section
  const summaryData: CellInput[][] = [
    [
      { content: 'Receita Total', styles: { fontStyle: 'bold' } },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(reportData.total_revenue),
        styles: {
          halign: 'right',
          textColor: colors.darkBlue,
          fontStyle: 'bold',
        },
      },
    ],
    [
      { content: 'Repasses', styles: { fontStyle: 'bold' } },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(
          reportData.total_fowarding_fee + reportData.company_commission
        ),
        styles: { halign: 'right', textColor: colors.red, fontStyle: 'bold' },
      },
    ],
    [
      { content: 'Taxa de Limpeza', styles: { fontStyle: 'bold' } },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(reportData.total_cleaning_fee),
        styles: { halign: 'right', textColor: colors.red, fontStyle: 'bold' },
      },
    ],
    [
      { content: 'Manutenções', styles: { fontStyle: 'bold' } },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(reportData.total_maintenance_fee),
        styles: { halign: 'right', textColor: colors.red, fontStyle: 'bold' },
      },
    ],
    [
      {
        content: 'Lucro',
        styles: { fontStyle: 'bold' },
      },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(
          reportData.total_revenue -
            (reportData.total_fowarding_fee +
              reportData.company_commission +
              reportData.total_cleaning_fee +
              reportData.total_maintenance_fee)
        ),
        styles: { halign: 'right', textColor: colors.green, fontStyle: 'bold' },
      },
    ],
    [
      { content: 'Diária Média', styles: { fontStyle: 'bold' } },
      {
        content: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(reportData.adr),
        styles: { halign: 'right' },
      },
    ],
    [
      { content: 'Noites', styles: { fontStyle: 'bold' } },
      {
        content: reportData.room_nights_sold,
        styles: { halign: 'right' },
      },
    ],
    [
      { content: 'Taxa de Ocupação (%)', styles: { fontStyle: 'bold' } },
      {
        content: `${reportData.occupancy_rate.toFixed(2)}%`,
        styles: { halign: 'right' },
      },
    ],
  ]

  autoTable(doc, {
    startY: cursorY,
    body: summaryData,
    theme: 'plain',
    styles: {
      cellPadding: 8,
      fontSize: 10,
      font: 'helvetica',
    },
    didDrawCell: (data) => {
      if (data.section === 'body') {
        doc.setDrawColor(colors.lightGray)
        doc.rect(
          data.cell.x,
          data.cell.y,
          data.cell.width,
          data.cell.height,
          'S'
        )
      }
    },
    didDrawPage: (data) => {
      // draw rounded background only when dimensions are stable
      const t: any = data.table
      const x = isFiniteNumber(t?.x) ? t.x : margin
      const y = isFiniteNumber(t?.y) ? t.y : data.settings.margin?.top ?? margin
      const w = isFiniteNumber(t?.width) ? t.width : pageWidth - margin * 2
      const h = isFiniteNumber(t?.height) ? t.height : 0

      doc.setFillColor(colors.lightGray)
      // Slight padding (0) to avoid clipping borders; tweak if you want
      safeRoundedRect(doc, x, y, w, h, 10, 10, 'F')
    },
  })
  cursorY = (doc as any).lastAutoTable.finalY + 40

  // Property Details
  for (const [propertyId, reservations] of Object.entries(
    reportData.properties_reservations
  )) {
    const propertyTotals = reservations.reduce(
      (acc, res) => {
        acc.totalNightCounts += res.night_count
        acc.totalRevenue += res.reserve_total
        acc.totalForwardFee += res.total_forward_fee + res.company_commission
        acc.totalCleaningFee += res.total_cleaning_fee
        acc.totalProfit +=
          res.reserve_total -
          (res.total_forward_fee +
            res.total_cleaning_fee +
            res.company_commission)
        return acc
      },
      {
        totalNightCounts: 0,
        totalRevenue: 0,
        totalForwardFee: 0,
        totalCleaningFee: 0,
        totalProfit: 0,
      }
    )

    if (cursorY > pageHeight - 150) {
      doc.addPage()
      cursorY = margin
    }

    doc
      .setFontSize(14)
      .setFont('helvetica', 'bold')
      .setTextColor(colors.darkBlue)
    doc.text(`Propriedade: ${propertyId}`, margin - 30, cursorY)
    cursorY += 8
    doc
      .setDrawColor(colors.divider)
      .line(margin - 30, cursorY, pageWidth - margin, cursorY)
    cursorY += 20

    const tableData = reservations.map((res) => {
      const profit =
        res.reserve_total -
        (res.total_forward_fee +
          res.company_commission +
          res.total_cleaning_fee)
      return [
        res.client.name,
        `${format(
          new Date(`${res.check_in_date}T00:00:00-03:00`),
          'dd/MM/yy'
        )} ${format(
          new Date(`${res.check_out_date}T00:00:00-03:00`),
          'dd/MM/yy'
        )}`,
        res.night_count,
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(res.reserve_total),
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(res.total_forward_fee + res.company_commission),
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(res.total_cleaning_fee),
        {
          content: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(profit),
          styles: {
            textColor: colors.green,
            fontStyle: 'bold',
          },
        },
      ]
    }) as CellInput[][]

    autoTable(doc, {
      startY: cursorY,
      head: [
        [
          'Hospede',
          'Período',
          'Noites',
          'Receita Total',
          'Repasse',
          'Taxa de Limpeza',
          'Lucro',
        ],
      ],
      body: tableData.concat([
        [
          null,
          {
            content: 'Total',
            styles: {
              fontStyle: 'bold',
            },
          },
          {
            content: propertyTotals.totalNightCounts,
            styles: {
              fontStyle: 'bold',
            },
          },
          {
            content: new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(propertyTotals.totalRevenue),
            styles: {
              fontStyle: 'bold',
            },
          },
          {
            content: new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(propertyTotals.totalForwardFee),
            styles: {
              fontStyle: 'bold',
            },
          },
          {
            content: new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(propertyTotals.totalCleaningFee),
            styles: {
              fontStyle: 'bold',
            },
          },
          {
            content: new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(propertyTotals.totalProfit),
            styles: {
              fontStyle: 'bold',
            },
          },
        ],
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: colors.lightBlue,
        textColor: colors.darkBlue,
        fontStyle: 'bold',
      },
      styles: { fontSize: 9, font: 'helvetica' },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'center', cellWidth: 60 },
        2: { halign: 'right' },
        3: { halign: 'right', textColor: colors.darkBlue },
        4: { halign: 'right', textColor: colors.red },
        5: { halign: 'right', textColor: colors.red },
        6: { halign: 'right' },
      },
      alternateRowStyles: {
        fillColor: colors.veryLightGray,
      },
    })
    cursorY = (doc as any).lastAutoTable.finalY + 30
  }

  addFooter()

  const fileName = `relatorio-${reportData.owner_name.replace(
    /\s/g,
    '_'
  )}-${format(new Date(), 'yyyy-MM-dd')}.pdf`
  doc.save(fileName)
  return fileName
}

// --- General Payout Report PDF ---
export function exportReportToPDF(
  payoutData: FinancialReport[],
  startDate: Date | null,
  endDate: Date | null
) {
  // Keep defaults for this report (jsPDF uses 'mm' by default); layout is simple and robust.
  const doc = new jsPDF()
  doc.setFont('helvetica')

  // Title
  doc.setFontSize(18).setTextColor(40, 40, 40)
  doc.text('Relatório de Repasse - Gestão de Imóveis', 14, 22)

  // Period
  if (startDate && endDate) {
    doc.setFontSize(12).setTextColor(100, 100, 100)
    doc.text(
      `Período: ${format(startDate, 'dd/MM/yyyy')} à ${format(
        endDate,
        'dd/MM/yyyy'
      )}`,
      14,
      32
    )
  }

  // Table
  const tableData = payoutData.map((item) => [
    item.owner_name || 'N/A',
    item.properties_count,
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(item.total_revenue),
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(item.total_fowarding_fee + item.company_commission),
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(
      item.total_revenue - (item.total_fowarding_fee + item.company_commission)
    ),
    `${item.occupancy_rate.toFixed(2)}%`,
  ])

  autoTable(doc, {
    startY: 40,
    head: [
      [
        'Proprietário',
        'Propriedades',
        'Receita Total',
        'Repasses',
        'Lucro',
        'Tx. de Ocup.',
      ],
    ],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [66, 139, 202] },
  })

  const fileName = `relatorio-geral-${format(new Date(), 'yyyy-MM-dd')}.pdf`
  doc.save(fileName)
  return fileName
}
